/**
 * Load more for the Product list section (grid layout).
 * Fetches the next page of the section via the Section Rendering API
 * and appends the new product cards to the existing grid.
 */
class ProductListLoadMore extends HTMLElement {
  connectedCallback() {
    this.page = 1;
    this.loading = false;
    this.button = this.querySelector('[data-load-more]');
    this.shownEl = this.querySelector('[data-load-more-shown]');
    this.statusEl = this.querySelector('[data-load-more-status]');
    this.button?.addEventListener('click', this.load);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.load);
  }

  get grid() {
    return this.closest('.section-resource-list')?.querySelector('.resource-list--grid');
  }

  get total() {
    return parseInt(this.dataset.total, 10) || 0;
  }

  load = async () => {
    const grid = this.grid;
    if (this.loading || !grid) return;

    this.loading = true;
    this.classList.add('is-loading');
    this.button?.setAttribute('aria-busy', 'true');

    const nextPage = this.page + 1;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('page', String(nextPage));

      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Load more failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newItems = doc.querySelectorAll('.resource-list--grid > .resource-list__item');

      let firstNew = null;
      newItems.forEach((item) => {
        const node = document.importNode(item, true);
        node.classList.add('is-loaded');
        grid.appendChild(node);
        if (!firstNew) firstNew = node;
      });

      this.page = nextPage;

      const shown = grid.querySelectorAll(':scope > .resource-list__item').length;
      if (this.shownEl) this.shownEl.textContent = String(shown);
      if (this.statusEl) this.statusEl.textContent = `${newItems.length} more products loaded`;

      // Move keyboard focus to the first new product for accessibility
      if (firstNew && this.button?.matches(':focus-visible')) {
        firstNew.querySelector('a[href]')?.focus({ preventScroll: true });
      }

      if (newItems.length === 0 || shown >= this.total) {
        this.hidden = true;
      }
    } catch (error) {
      console.error(error);
      if (this.statusEl) this.statusEl.textContent = 'Could not load more products. Please try again.';
    } finally {
      this.loading = false;
      this.classList.remove('is-loading');
      this.button?.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('product-list-load-more')) {
  customElements.define('product-list-load-more', ProductListLoadMore);
}
